import { useCallback, useEffect, useRef, useState } from "react";

import {
  dashcamProtocol,
  dashcamSmsSupported,
  requestDashcamLiveVideo,
  requestDashcamLiveVideoOff,
  type DashcamCommand,
  type DashcamCommandTransport,
  type DashcamUnit,
} from "../services/dashcam";
import {
  isSuccessStatus,
  useDeviceCommandsStore,
  type CommandStatus,
} from "../store/admin/deviceCommandsStore";
import { useUserStore } from "../store/userStore";
import { ACCESS_NODES } from "../config/access/nodes";
import { useCanWrite } from "./useCanWrite";
import { useEsCliente } from "./useEsCliente";

export const DASHCAM_LIVE_VIDEO_PHASES = [
  "idle",
  "sending",
  "waiting",
  "ready",
  "failed",
] as const;

export type DashcamLiveVideoPhase = (typeof DASHCAM_LIVE_VIDEO_PHASES)[number];

export const DASHCAM_SMS_SUPPORT = ["checking", "available", "unavailable"] as const;

export type DashcamSmsSupport = (typeof DASHCAM_SMS_SUPPORT)[number];

export interface DashcamLiveVideo {
  readOnly: boolean;
  phase: DashcamLiveVideoPhase;
  status: CommandStatus | null;
  error: string | null;
  transport: DashcamCommandTransport;
  smsSupport: DashcamSmsSupport;
  ready: boolean;
  pending: boolean;
  reportStream: (key: string, alive: boolean | null) => void;
  retry: () => void;
  force: () => void;
}

const SMS_PASSWORD =
  (import.meta.env.VITE_DASHCAM_SMS_PASSWORD as string | undefined)?.trim() || null;

const HEARTBEAT_MS = 60000;
const DELIVERED_GRACE_MS = 8000;
const WAITING_TIMEOUT_MS = 45000;

interface LiveVideoAttempt {
  transport: DashcamCommandTransport;
  smsPassword: string | null;
  nonce: number;
}

interface LiveVideoResult {
  requestId: string | null;
  deviceId: string | null;
  brandId: string | null;
  status: CommandStatus | null;
  error: string | null;
}

const GPRS_ATTEMPT: LiveVideoAttempt = { transport: "gprs", smsPassword: null, nonce: 0 };

const commandErrorMessage = (error: unknown): string => {
  const detail = error as { response?: { data?: { message?: string } }; message?: string };
  return detail.response?.data?.message ?? detail.message ?? "commandFailed";
};

const storePayload = (command: DashcamCommand, fallbackDeviceId: string | null) => ({
  device_id: command.device_id ?? fallbackDeviceId ?? "",
  company_id: command.company_id ?? null,
  vehicle_id: command.vehicle_id ?? null,
  remolque_id: command.remolque_id ?? null,
  command: {
    request_id: command.request_id ?? "",
    imei: command.imei,
    brand_id: command.brand_id,
    brand: command.brand,
    model: command.model,
    command_doc_id: command.command_doc_id,
    command_code: command.command_code ?? "live_video_on",
    command_name: command.command_name,
    status: command.status ?? "queued",
    error: command.error,
    raw_request: command.raw_request,
    raw_response: command.raw_response,
    sent_at: command.sent_at,
    finished_at: command.finished_at,
  },
});

export function useDashcamLiveVideo(unit: DashcamUnit | null): DashcamLiveVideo {
  const deviceId = unit?.deviceId ?? null;
  const imei = unit?.imei ?? null;
  /*
    El cliente enciende SU cámara, pero sigue sin el panel de acciones.

    El video en vivo se pide con un comando al equipo, y los comandos cuelgan
    del nodo `lite.actions` — el mismo que abre el corte de motor. El rol
    `cliente` no lo tiene, y con razón: nadie quiere que el dueño de la unidad
    pueda apagarle el motor a su propio camión desde el teléfono. Pero entonces
    tampoco podía ver la cámara que paga, y el panel le contestaba «pídeselo a
    un administrador» sobre su propia unidad.

    Este hook manda EXACTAMENTE dos comandos, `live_video_on` y
    `live_video_off`, así que ampliarlo aquí no le abre nada más: el panel de
    acciones y la ficha siguen consultando `lite.actions` sin cambios.

    Y sólo ve sus unidades: con el recorte de `available_devices` el cliente no
    recibe equipos ajenos, así que la cámara que puede encender es la suya.
  */
  const esCliente = useEsCliente();
  const canSendCommands = useCanWrite(ACCESS_NODES.liteActions) || esCliente;
  const commandable =
    Boolean(unit) &&
    dashcamProtocol(unit?.dashCam) === "whep" &&
    Boolean(deviceId || imei);
  const needed = canSendCommands && commandable;

  const userId = useUserStore((state) => state.user?.id ?? null);
  const addCommand = useDeviceCommandsStore((state) => state.addCommand);

  const [attempt, setAttempt] = useState<LiveVideoAttempt>(GPRS_ATTEMPT);
  const [result, setResult] = useState<LiveVideoResult | null>(null);
  const [sending, setSending] = useState(false);
  const [waitOutcome, setWaitOutcome] = useState<"proceed" | "timeout" | null>(null);
  const [streamStates, setStreamStates] = useState<Record<string, boolean>>({});
  const [smsSupport, setSmsSupport] = useState<DashcamSmsSupport>("checking");

  const activeKeyRef = useRef<string | null>(null);
  const userIdRef = useRef<string | null>(userId);
  const turnedOnRef = useRef(false);
  const heartbeatBusyRef = useRef(false);
  const lastSentAtRef = useRef(0);
  const inFlightRef = useRef<Promise<unknown>>(Promise.resolve());

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  const reportStream = useCallback((key: string, alive: boolean | null) => {
    setStreamStates((prev) => {
      if (alive === null) {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return prev[key] === alive ? prev : { ...prev, [key]: alive };
    });
  }, []);

  const watching = Object.values(streamStates).some(Boolean);

  useEffect(() => {
    if (!needed) {
      setResult(null);
      return;
    }

    let cancelled = false;
    setSending(true);
    setResult(null);
    setWaitOutcome(null);
    turnedOnRef.current = true;
    lastSentAtRef.current = Date.now();

    const request = requestDashcamLiveVideo({
      deviceId,
      imei,
      transport: attempt.transport,
      smsPassword: attempt.smsPassword,
      createdByUserId: userId,
    });

    inFlightRef.current = request.catch(() => undefined);

    request
      .then((response) => {
        const command = response?.command ?? null;
        command?.request_id && addCommand(storePayload(command, deviceId));
        command?.status && !isSuccessStatus(command.status) && (turnedOnRef.current = false);
        if (cancelled) return;
        setResult({
          requestId: command?.request_id ?? null,
          deviceId: command?.device_id ?? deviceId,
          brandId: command?.brand_id ?? null,
          status: command?.status ?? null,
          error: command?.error ?? null,
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setResult({
          requestId: null,
          deviceId: null,
          brandId: null,
          status: null,
          error: commandErrorMessage(error),
        });
      })
      .finally(() => {
        cancelled || setSending(false);
      });

    return () => {
      cancelled = true;
    };
  }, [needed, deviceId, imei, attempt, userId, addCommand]);

  useEffect(() => {
    if (!needed) {
      activeKeyRef.current = null;
      return;
    }

    const key = `${deviceId ?? ""}|${imei ?? ""}`;
    activeKeyRef.current = key;

    return () => {
      if (!turnedOnRef.current) return;
      turnedOnRef.current = false;
      activeKeyRef.current = null;

      void inFlightRef.current.then(() =>
        activeKeyRef.current === key
          ? undefined
          : requestDashcamLiveVideoOff({
              deviceId,
              imei,
              createdByUserId: userIdRef.current,
            }).catch(() => undefined),
      );
    };
  }, [needed, deviceId, imei]);

  const tracked = useDeviceCommandsStore((state) =>
    result?.requestId && result.deviceId
      ? ((state.commandsByDevice[result.deviceId] ?? []).find(
          (command) => command.request_id === result.requestId,
        ) ?? null)
      : null,
  );

  const brandId = tracked?.brand_id ?? result?.brandId ?? null;

  const reported = tracked?.status ?? result?.status ?? null;
  const status: CommandStatus | null = waitOutcome === "timeout" ? "timeout" : reported;
  const reportedError = tracked?.error ?? result?.error ?? null;

  const failed =
    !sending &&
    (status ? !isSuccessStatus(status) : Boolean(result && reportedError));

  const phase: DashcamLiveVideoPhase = !needed
    ? "ready"
    : failed
      ? "failed"
      : sending
        ? "sending"
        : status === "ack" || waitOutcome === "proceed"
          ? "ready"
          : status
            ? "waiting"
            : "idle";

  useEffect(() => {
    if (phase !== "waiting") return;

    const delivered = reported === "sent";
    const timer = window.setTimeout(
      () => setWaitOutcome(delivered ? "proceed" : "timeout"),
      delivered ? DELIVERED_GRACE_MS : WAITING_TIMEOUT_MS,
    );

    return () => window.clearTimeout(timer);
  }, [phase, reported]);

  const gaveUp = Object.values(streamStates).some((alive) => !alive);
  const needsSmsCheck = phase === "failed" || gaveUp;

  useEffect(() => {
    if (!needsSmsCheck) return;

    if (!brandId) {
      setSmsSupport("available");
      return;
    }

    let cancelled = false;
    setSmsSupport("checking");

    void dashcamSmsSupported(brandId).then((supported) => {
      cancelled || setSmsSupport(supported === false ? "unavailable" : "available");
    });

    return () => {
      cancelled = true;
    };
  }, [needsSmsCheck, brandId]);

  useEffect(() => {
    if (!needed || phase !== "ready" || !watching) return;

    const renew = () => {
      if (document.visibilityState !== "visible" || heartbeatBusyRef.current) return;
      if (Date.now() - lastSentAtRef.current < HEARTBEAT_MS) return;

      heartbeatBusyRef.current = true;
      lastSentAtRef.current = Date.now();

      const request = requestDashcamLiveVideo({
        deviceId,
        imei,
        createdByUserId: userIdRef.current,
      });
      inFlightRef.current = request.catch(() => undefined);

      void request
        .then((response) => {
          const command = response?.command ?? null;
          if (!command) return;
          command.request_id && addCommand(storePayload(command, deviceId));
          if (!command.status || isSuccessStatus(command.status)) return;
          turnedOnRef.current = false;
          setResult({
            requestId: command.request_id ?? null,
            deviceId: command.device_id ?? deviceId,
            brandId: command.brand_id ?? null,
            status: command.status,
            error: command.error ?? null,
          });
        })
        .catch(() => undefined)
        .finally(() => {
          heartbeatBusyRef.current = false;
        });
    };

    const timer = window.setInterval(renew, HEARTBEAT_MS);

    const onVisibility = () => document.visibilityState === "visible" && renew();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [needed, phase, watching, deviceId, imei, addCommand]);

  const retry = useCallback(
    () => setAttempt((prev) => ({ transport: "gprs", smsPassword: null, nonce: prev.nonce + 1 })),
    [],
  );

  const force = useCallback(
    () =>
      setAttempt((prev) => ({ transport: "sms", smsPassword: SMS_PASSWORD, nonce: prev.nonce + 1 })),
    [],
  );

  return {
    readOnly: commandable && !canSendCommands,
    phase,
    status,
    error: failed ? reportedError : null,
    transport: attempt.transport,
    smsSupport,
    ready: phase === "ready",
    pending: phase === "sending" || phase === "waiting" || phase === "idle",
    reportStream,
    retry,
    force,
  };
}

export default useDashcamLiveVideo;
